// ── Shared ActivityHistory component used across all 3 dashboards ──
// Props:
//   bookings     — array of all bookings (all statuses)
//   canvasItems  — array of canvas submissions
//   role         — 'CLIENT' | 'EXPERT' | 'ADMIN'

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Video, Mic, MessageCircle, Calendar, Clock,
  CheckCircle2, XCircle, RotateCcw, FileText,
  ChevronDown, ChevronUp, Users, BookOpen,
  Star, TrendingUp, Tag,
} from 'lucide-react';

const SESSION_ICON = { VIDEO: Video, AUDIO: Mic, CHAT: MessageCircle };

const STATUS_BADGE = {
  COMPLETED:   'bg-green-50 text-green-700 border-green-200',
  CONFIRMED:   'bg-forest-50 text-forest-700 border-forest-200',
  PENDING:     'bg-amber-50 text-amber-700 border-amber-200',
  CANCELLED:   'bg-cream-100 text-forest-400 border-cream-200',
  RESCHEDULED: 'bg-purple-50 text-purple-700 border-purple-200',
};

const STATUS_ICON = {
  COMPLETED:   CheckCircle2,
  CANCELLED:   XCircle,
  RESCHEDULED: RotateCcw,
};

export const ActivityHistory = ({ bookings = [], canvasItems = [], role = 'CLIENT' }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter]         = useState('ALL');

  const FILTERS = ['ALL', 'COMPLETED', 'CONFIRMED', 'CANCELLED', 'RESCHEDULED'];

  const filtered = filter === 'ALL'
    ? bookings
    : bookings.filter(b => b.status === filter);

  // Stats summary
  const completed   = bookings.filter(b => b.status === 'COMPLETED').length;
  const cancelled   = bookings.filter(b => b.status === 'CANCELLED').length;
  const rescheduled = bookings.filter(b => b.status === 'RESCHEDULED').length;
  const totalMins   = bookings
    .filter(b => b.status === 'COMPLETED')
    .reduce((s, b) => s + (b.duration || 0), 0);

  // Unique experts engaged (for client view)
  const expertsEngaged = role === 'CLIENT'
    ? [...new Map(
        bookings
          .filter(b => b.expertProfile?.id)
          .map(b => [b.expertProfile.id, {
            id:   b.expertProfile.id,
            name: `${b.expertProfile.user?.firstName ?? ''} ${b.expertProfile.user?.lastName ?? ''}`.trim(),
            specs: (() => {
              try {
                const s = b.expertProfile.specializations;
                return Array.isArray(s) ? s : JSON.parse(s || '[]');
              } catch { return []; }
            })(),
            sessions: bookings.filter(x => x.expertProfile?.id === b.expertProfile.id && x.status === 'COMPLETED').length,
          }])
      ).values()]
    : [];

  // Unique clients engaged (for expert view)
  const clientsEngaged = role === 'EXPERT'
    ? [...new Map(
        bookings
          .filter(b => b.clientProfile?.id && b.status === 'COMPLETED')
          .map(b => [b.clientProfile.id, {
            id:   b.clientProfile.id,
            name: `${b.clientProfile.user?.firstName ?? ''} ${b.clientProfile.user?.lastName ?? ''}`.trim(),
            business: b.clientProfile.businessName || b.clientProfile.user?.businessName || '',
            sessions: bookings.filter(x => x.clientProfile?.id === b.clientProfile.id && x.status === 'COMPLETED').length,
          }])
      ).values()]
    : [];

  return (
    <div className="space-y-6">

      {/* ── Summary stats ─────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon: CheckCircle2, label: 'Completed',    value: completed,            color: 'text-green-600 bg-green-50' },
          { icon: Clock,        label: 'Total Hours',  value: `${Math.floor(totalMins/60)}h ${totalMins%60}m`, color: 'text-blue-600 bg-blue-50' },
          { icon: RotateCcw,    label: 'Rescheduled',  value: rescheduled,          color: 'text-purple-600 bg-purple-50' },
          { icon: XCircle,      label: 'Cancelled',    value: cancelled,            color: 'text-rust-500 bg-rust-50' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="bg-white border border-cream-200 rounded-2xl p-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
              <Icon size={16} />
            </div>
            <div>
              <div className="font-serif text-xl font-bold text-forest-900">{value}</div>
              <div className="text-[10px] text-forest-400 uppercase tracking-widest">{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Filter tabs ───────────────────────────────────── */}
      <div className="flex gap-2 flex-wrap">
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-all ${
              filter === f
                ? 'bg-forest-800 text-cream-50 border-forest-800'
                : 'bg-white text-forest-600 border-cream-200 hover:border-forest-400'
            }`}>
            {f === 'ALL' ? `All (${bookings.length})` : f}
          </button>
        ))}
      </div>

      {/* ── Session history list ──────────────────────────── */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="bg-white border border-cream-200 rounded-2xl p-10 text-center text-forest-400">
            <Calendar size={32} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No sessions found</p>
          </div>
        ) : filtered.map(b => {
          const Icon      = SESSION_ICON[b.sessionType] ?? Video;
          const SIcon     = STATUS_ICON[b.status];
          const isOpen    = expandedId === b.id;
          const otherName = role === 'EXPERT'
            ? `${b.clientProfile?.user?.firstName ?? ''} ${b.clientProfile?.user?.lastName ?? ''}`.trim() || 'Client'
            : `${b.expertProfile?.user?.firstName ?? ''} ${b.expertProfile?.user?.lastName ?? ''}`.trim() || 'Expert';
          const otherLabel = role === 'EXPERT' ? 'Client' : 'Expert';
          const date = b.scheduledAt
            ? new Date(b.scheduledAt).toLocaleString('en-GH', { dateStyle: 'long', timeStyle: 'short' })
            : '—';
          const specs = (() => {
            try {
              const s = b.expertProfile?.specializations;
              return Array.isArray(s) ? s : JSON.parse(s || '[]');
            } catch { return []; }
          })();

          return (
            <div key={b.id} className="bg-white border border-cream-200 rounded-2xl overflow-hidden hover:border-forest-200 transition-colors">
              {/* Row */}
              <div className="flex items-start gap-4 p-4 cursor-pointer" onClick={() => setExpandedId(isOpen ? null : b.id)}>
                <div className="w-10 h-10 bg-forest-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon size={16} className="text-forest-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div>
                      <p className="text-sm font-semibold text-forest-900">
                        {b.sessionType} · {otherLabel}: {otherName}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-forest-500 flex-wrap">
                        <span className="flex items-center gap-1"><Calendar size={10} /> {date}</span>
                        <span className="flex items-center gap-1"><Clock size={10} /> {b.duration ?? '—'} mins</span>
                        {(b.rescheduleCount ?? 0) > 0 && (
                          <span className="flex items-center gap-1 text-purple-500">
                            <RotateCcw size={10} /> Rescheduled {b.rescheduleCount}×
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-[10px] font-medium border px-2 py-0.5 rounded-full ${STATUS_BADGE[b.status] ?? ''}`}>
                        {b.status}
                      </span>
                      {isOpen ? <ChevronUp size={14} className="text-forest-400" /> : <ChevronDown size={14} className="text-forest-400" />}
                    </div>
                  </div>
                </div>
              </div>

              {/* Expanded detail */}
              {isOpen && (
                <div className="border-t border-cream-100 bg-cream-50 p-4 space-y-3">

                  {/* Notes / Topics */}
                  {b.notes && (
                    <div className="flex items-start gap-2">
                      <Tag size={13} className="text-forest-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-forest-400 mb-1">Topics / Notes</p>
                        <p className="text-xs text-forest-700 leading-relaxed italic">"{b.notes}"</p>
                      </div>
                    </div>
                  )}

                  {/* Expert specializations (client view) */}
                  {role === 'CLIENT' && specs.length > 0 && (
                    <div className="flex items-start gap-2">
                      <Star size={13} className="text-forest-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-forest-400 mb-1.5">Expert's Specializations</p>
                        <div className="flex flex-wrap gap-1.5">
                          {specs.map((s, i) => (
                            <span key={i} className="text-[10px] bg-forest-50 text-forest-600 border border-forest-100 px-2 py-0.5 rounded-full">{s}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Level context */}
                  {b.level?.name && (
                    <div className="flex items-center gap-2">
                      <TrendingUp size={13} className="text-forest-400 flex-shrink-0" />
                      <p className="text-xs text-forest-600">
                        <span className="font-medium">Tourent Cycle Level:</span> {b.level.name}
                      </p>
                    </div>
                  )}

                  {/* Session recording link */}
                  {b.status === 'COMPLETED' && b.session?.id && (
                    <div className="flex items-center gap-2">
                      <Video size={13} className="text-forest-400 flex-shrink-0" />
                      <Link
                        to={role === 'EXPERT'
                          ? `/expert/sessions/${b.session.id}/recording`
                          : `/sessions/${b.session.id}/recording`}
                        className="text-xs text-forest-600 hover:text-forest-900 font-medium underline underline-offset-2 transition-colors">
                        View recording & transcript →
                      </Link>
                    </div>
                  )}

                  {/* Booking detail link */}
                  <div className="flex items-center gap-2">
                    <FileText size={13} className="text-forest-400 flex-shrink-0" />
                    <Link
                      to={role === 'EXPERT' ? `/expert/bookings/${b.id}` : `/bookings/${b.id}`}
                      className="text-xs text-forest-500 hover:text-forest-800 transition-colors">
                      View booking details →
                    </Link>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Canvas / Tourent Cycle Activities ─────────────── */}
      {role === 'CLIENT' && canvasItems.length > 0 && (
        <div>
          <h4 className="font-serif text-lg font-semibold text-forest-900 mb-3 flex items-center gap-2">
            <BookOpen size={18} className="text-forest-600" /> Tourent Cycle Canvas Activities
          </h4>
          <div className="space-y-2">
            {canvasItems.map((s, i) => (
              <div key={s.id ?? i} className="bg-white border border-cream-200 rounded-xl p-4 flex items-center gap-4">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  s.status === 'GRADED' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'
                }`}>
                  <BookOpen size={15} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-forest-900 truncate">
                    {s.canvasActivity?.title ?? s.title ?? 'Canvas Activity'}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-forest-400 mt-0.5 flex-wrap">
                    {s.submittedAt && (
                      <span>Submitted: {new Date(s.submittedAt).toLocaleDateString('en-GH', { dateStyle: 'medium' })}</span>
                    )}
                    {s.score != null && <span className="text-forest-600 font-medium">Score: {s.score}/{s.canvasActivity?.maxScore ?? 100}</span>}
                  </div>
                </div>
                <span className={`text-[10px] font-medium border px-2 py-0.5 rounded-full flex-shrink-0 ${
                  s.status === 'GRADED'   ? 'bg-green-50 text-green-700 border-green-200'
                  : s.status === 'REVISION_REQUESTED' ? 'bg-rust-50 text-rust-600 border-rust-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
                }`}>
                  {s.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Experts engaged (client view) ─────────────────── */}
      {role === 'CLIENT' && expertsEngaged.length > 0 && (
        <div>
          <h4 className="font-serif text-lg font-semibold text-forest-900 mb-3 flex items-center gap-2">
            <Users size={18} className="text-forest-600" /> Experts You've Engaged
          </h4>
          <div className="grid sm:grid-cols-2 gap-3">
            {expertsEngaged.map(e => (
              <div key={e.id} className="bg-white border border-cream-200 rounded-2xl p-4 flex items-start gap-3">
                <div className="w-10 h-10 bg-forest-700 rounded-xl flex items-center justify-center text-cream-50 font-serif font-bold text-sm flex-shrink-0">
                  {e.name?.[0] ?? '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-forest-900">{e.name || 'Expert'}</p>
                  <p className="text-xs text-forest-400 mt-0.5">{e.sessions} session{e.sessions !== 1 ? 's' : ''} completed</p>
                  {e.specs.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {e.specs.slice(0, 3).map((s, i) => (
                        <span key={i} className="text-[10px] bg-forest-50 text-forest-600 border border-forest-100 px-2 py-0.5 rounded-full">{s}</span>
                      ))}
                    </div>
                  )}
                </div>
                <Link to={`/experts/${e.id}`} className="text-xs text-forest-400 hover:text-forest-700 flex-shrink-0 transition-colors">
                  View →
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Clients engaged (expert view) ─────────────────── */}
      {role === 'EXPERT' && clientsEngaged.length > 0 && (
        <div>
          <h4 className="font-serif text-lg font-semibold text-forest-900 mb-3 flex items-center gap-2">
            <Users size={18} className="text-forest-600" /> Clients You've Mentored
          </h4>
          <div className="grid sm:grid-cols-2 gap-3">
            {clientsEngaged.map(c => (
              <div key={c.id} className="bg-white border border-cream-200 rounded-2xl p-4 flex items-start gap-3">
                <div className="w-10 h-10 bg-cream-200 rounded-xl flex items-center justify-center text-forest-700 font-serif font-bold text-sm flex-shrink-0">
                  {c.name?.[0] ?? '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-forest-900">{c.name || 'Client'}</p>
                  {c.business && <p className="text-xs text-forest-400 mt-0.5">{c.business}</p>}
                  <p className="text-xs text-forest-400 mt-0.5">{c.sessions} session{c.sessions !== 1 ? 's' : ''} completed</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityHistory;